import type {
  QuestionLiveResultsResult,
  QuestionTurnResult,
} from './shared-responder-v2-core-gateway.types';
import type {
  DeviceGateVerdict,
  ResponseMode,
} from './shared-responder-v2-core-session.consts';
import type { ShortTitle } from './shared-responder-v2-core-short-title.types';
import type { ResponderDraftEvent } from './shared-responder-v2-core-thread-behaviour';
import type { SiteVisit } from './shared-responder-v2-core-thread.consts';
import type {
  ResponderTurn,
  TurnEdit,
} from './shared-responder-v2-core-thread.types';

/**
 * Everything that can change the session, split into responder intents and
 * gateway results. The reducer is the only place the two meet, and it is
 * synchronous — every async concern lives behind the gateway seam, which is why
 * the whole machine is testable by direct calls.
 *
 * Gateway results carry `seq`. The reducer discards any result whose `seq` is
 * behind the thread's current `requestSeq`, which is how an edit cancels a
 * reply that was already in flight.
 */
export type ResponderEvent =
  | { readonly type: 'deviceGateResolved'; readonly verdict: DeviceGateVerdict }
  | { readonly type: 'introAcknowledged' }
  | { readonly type: 'introReturnRequested' }
  | { readonly type: 'modeChanged'; readonly mode: ResponseMode }
  | {
      readonly type: 'draftEdited';
      readonly questionId: string;
      readonly event: ResponderDraftEvent;
    }
  | { readonly type: 'answerSubmitted'; readonly questionId: string }
  | { readonly type: 'continuePressed' }
  | { readonly type: 'jumpRequested'; readonly stepIndex: number }
  | { readonly type: 'answerEditRequested'; readonly stepIndex: number }
  | {
      readonly type: 'sessionResumed';
      readonly stepIndex: number;
      readonly isComplete: boolean;
    }
  | { readonly type: 'editCancelled' }
  | { readonly type: 'distributionToggled'; readonly questionId: string }
  | {
      readonly type: 'completionBlockReplaced';
      readonly questionIds: readonly string[];
    }
  | { readonly type: 'sessionErrored' }
  | { readonly type: 'labelsResolved'; readonly labels: readonly ShortTitle[] }
  | {
      readonly type: 'threadInitiated';
      readonly questionId: string;
      readonly threadId: string;
      readonly seq: number;
      readonly priorTurns: readonly ResponderTurn[];
      /** `OpenQuestionResult.isComplete`: this question's session is closed. */
      readonly isComplete: boolean;
    }
  | {
      readonly type: 'threadMessageAccepted';
      readonly questionId: string;
      readonly seq: number;
      readonly result: QuestionTurnResult;
      readonly liveResults?: QuestionLiveResultsResult | null;
    }
  | {
      readonly type: 'followUpSkipped';
      readonly questionId: string;
      readonly seq: number;
    }
  | {
      readonly type: 'threadFailed';
      readonly questionId: string;
      readonly seq: number;
      readonly message: string;
    }
  | { readonly type: 'turnRetryRequested'; readonly questionId: string }
  | ({ readonly type: 'turnEditSubmitted' } & TurnEditSubmission)
  | {
      readonly type: 'siteVisitRecorded';
      readonly questionId: string;
      readonly visit: SiteVisit;
      /** The site opened, so it can carry forward to a later `contentUrl: null` task. */
      readonly url: string | null;
    };

export type ResponderEventType = ResponderEvent['type'];

export function deviceGateResolved(verdict: DeviceGateVerdict): ResponderEvent {
  return { type: 'deviceGateResolved', verdict };
}

export function introAcknowledged(): ResponderEvent {
  return { type: 'introAcknowledged' };
}

export function introReturnRequested(): ResponderEvent {
  return { type: 'introReturnRequested' };
}

export function modeChanged(mode: ResponseMode): ResponderEvent {
  return { type: 'modeChanged', mode };
}

export function draftEdited(
  questionId: string,
  event: ResponderDraftEvent
): ResponderEvent {
  return { type: 'draftEdited', questionId, event };
}

export function answerSubmitted(questionId: string): ResponderEvent {
  return { type: 'answerSubmitted', questionId };
}

export function continuePressed(): ResponderEvent {
  return { type: 'continuePressed' };
}

export function jumpRequested(stepIndex: number): ResponderEvent {
  return { type: 'jumpRequested', stepIndex };
}

/**
 * Opens the edit of one step's answer — a rail click on an earlier step, or
 * "Edit answer" on the question on screen — so `stepIndex` names the step
 * rather than assuming the one immediately behind the cursor.
 */
export function answerEditRequested(stepIndex: number): ResponderEvent {
  return { type: 'answerEditRequested', stepIndex };
}

export function sessionResumed(
  stepIndex: number,
  isComplete: boolean
): ResponderEvent {
  return { type: 'sessionResumed', stepIndex, isComplete };
}

export function editCancelled(): ResponderEvent {
  return { type: 'editCancelled' };
}

export function distributionToggled(questionId: string): ResponderEvent {
  return { type: 'distributionToggled', questionId };
}

export function completionBlockReplaced(
  questionIds: readonly string[]
): ResponderEvent {
  return { type: 'completionBlockReplaced', questionIds };
}

export function sessionErrored(): ResponderEvent {
  return { type: 'sessionErrored' };
}

export function labelsResolved(labels: readonly ShortTitle[]): ResponderEvent {
  return { type: 'labelsResolved', labels };
}

export function threadInitiated(
  questionId: string,
  threadId: string,
  seq: number,
  priorTurns: readonly ResponderTurn[] = [],
  isComplete = false
): ResponderEvent {
  return {
    type: 'threadInitiated',
    questionId,
    threadId,
    seq,
    priorTurns,
    isComplete,
  };
}

export function threadMessageAccepted(
  questionId: string,
  seq: number,
  result: QuestionTurnResult,
  liveResults: QuestionLiveResultsResult | null = null
): ResponderEvent {
  return {
    type: 'threadMessageAccepted',
    questionId,
    seq,
    result,
    ...(liveResults !== null ? { liveResults } : {}),
  };
}

export function followUpSkipped(
  questionId: string,
  seq: number
): ResponderEvent {
  return { type: 'followUpSkipped', questionId, seq };
}

export function threadFailed(
  questionId: string,
  seq: number,
  message: string
): ResponderEvent {
  return { type: 'threadFailed', questionId, seq, message };
}

/**
 * Re-arms a failed thread for one more attempt at the answer it already holds.
 * It carries no `seq` of its own because it *sets* the new one: bumping
 * `requestSeq` here is what makes the abandoned attempt's late reply stale, so
 * the caller must issue its gateway request at the bumped value, exactly as
 * `answerSubmitted` already requires.
 */
export function turnRetryRequested(questionId: string): ResponderEvent {
  return { type: 'turnRetryRequested', questionId };
}

export interface TurnEditSubmission extends TurnEdit {
  readonly questionId: string;
}

/**
 * Like `answerSubmitted` it bumps `requestSeq`, so the caller issues its
 * `editQuestionMessage` at the bumped value.
 */
export function turnEditSubmitted(
  submission: TurnEditSubmission
): ResponderEvent {
  return { type: 'turnEditSubmitted', ...submission };
}

/**
 * Reports what the responder has done with a question's site, which is what
 * releases the site gate — the kernel cannot see an embed open, so the surface
 * that opens one says so here.
 *
 * `opened` is terminal: an embed that errors after the responder has already
 * seen the site has not un-seen it, and letting a later `unavailable` overwrite
 * it would take back the revisit affordance they had earned.
 */
export function siteVisitRecorded(
  questionId: string,
  visit: SiteVisit,
  url: string | null = null
): ResponderEvent {
  return { type: 'siteVisitRecorded', questionId, visit, url };
}
